import {
  Lit3formBlock,
  html,
  unsafeHTML,
  ifDefined,
  classMap,
} from '@Root/lib/lit-3form-block';
import { styleMap } from 'lit/directives/style-map.js';
import { styles } from './css';

import '@Helpers/vimeo-video';
import {
  handleAdobeNavEvent,
  analyticsCtaDataObject,
} from '../../../helpers/adobe-analytics';

export class Bonnie extends Lit3formBlock {
  static get characterName() {
    return 'bonnie';
  }

  static get characterProperties() {
    return {
      header: { type: String },
      description: { type: String },
      cta: { type: String },
      backgroundColorOverride: { type: String },
      textColorOverride: { type: String },
    };
  }

  static get characterStyles() {
    return [...styles];
  }

  constructor() {
    super();
    this.backgroundColorOverride =
      this.content.background_color_override || undefined;
    this.textColorOverride = this.content.text_color_override || undefined;
  }

  render() {
    const styles = {
      backgroundColor: this.backgroundColorOverride,
      color: this.textColorOverride,
    };
    return html`
      <div class="block-wrapper">
        <div
          class="${classMap({
            'block-grid': true,
            'bonnie-layout': true,
            'has-subheader': !!this.subHeaderText,
            'mobile-rows-1': this.oneRowNeeded,
            'mobile-rows-2': this.twoRowsNeeded,
            'mobile-rows-3': this.threeRowsNeeded,
            'mobile-rows-4': this.fourRowsNeeded,
          })}"
          style=${styleMap(styles)}
        >
          ${this.subHeaderHTML} ${this.headerHTML} ${this.descriptionHTML}
          ${this.ctaHTML}
          ${this.videoId
            ? html`
                <div class="video media">
                  <vimeo-video
                    videoid="${this.videoId}"
                    videoWidth="1500"
                    minHeight="${this.videoHeight}"
                  ></vimeo-video>
                </div>
              `
            : html`
                <div class="image media">
                  <a
                    href=${ifDefined(
                      this.hasCta ? this.content.cta.url : undefined
                    )}
                    @click="${event => {
                      event.preventDefault();
                      handleAdobeNavEvent({
                        navString: 'NAV|${this.headerText}|',
                        destinationUrl: this.content?.cta?.url,
                        event,
                      });
                    }}"
                  >
                    <img src="${this.imageUrl}" />
                  </a>
                </div>
              `}
          ${this.horizontalRule}
        </div>
      </div>
    `;
  }

  get subHeaderHTML() {
    return this.subHeaderText
      ? html`
          <p class="subheader">${unsafeHTML(this.subHeaderText)}</p>
        `
      : ``;
  }

  get headerHTML() {
    return this.headerText
      ? html`
          <div class="header details-row">
            <h2>${unsafeHTML(this.headerText)}</h2>
          </div>
        `
      : ``;
  }

  get descriptionHTML() {
    return this.descriptionText
      ? html`
          <p class="description details-row">
            ${unsafeHTML(this.descriptionText)}
          </p>
        `
      : ``;
  }

  get ctaHTML() {
    return this.hasCta
      ? html`
          <cta-button
            class="cta-btn details-row"
            cta-data=${this.ctaInfo}
            cta-type="primary"
            .ctaAnalyticsData=${analyticsCtaDataObject(
              this.ctaInfo,
              this.headerText
            )}
          ></cta-button>
        `
      : ``;
  }

  get ctaInfo() {
    return JSON.stringify(this.content.cta);
  }

  get hasCta() {
    return this.content.cta && !!this.content.cta.text;
  }

  get headerText() {
    return this.withLineBreaks(this.content.header) || '';
  }

  get subHeaderText() {
    return this.withLineBreaks(this.content.sub_header) || '';
  }

  get descriptionText() {
    return this.withLineBreaks(this.content.description) || '';
  }

  get videoId() {
    if (!!this.content.video_url && this.content.video_url.includes('vimeo')) {
      return this.content.video_url.match(
        /^https?:\/\/(?:.*?)\.?(vimeo)\.com\/(\d+).*$/
      )[2];
    } else {
      return '';
    }
  }

  get imageUrl() {
    return !!this.content.image
      ? this.imagebossUrl(this.content.image.src, null, 1500)
      : '';
  }

  get videoHeight() {
    const useMobile = !window.matchMedia('(min-width: 600px)').matches;
    return this.videoId ? (useMobile ? 350 : 700) : null;
  }

  get oneRowNeeded() {
    return this.mobileRowsNeeded === 1;
  }

  get twoRowsNeeded() {
    return this.mobileRowsNeeded === 2;
  }

  get threeRowsNeeded() {
    return this.mobileRowsNeeded === 3;
  }

  get fourRowsNeeded() {
    return this.mobileRowsNeeded === 4;
  }

  get mobileRowsNeeded() {
    let count = 0;
    if (!!this.subHeaderText) count++;
    if (!!this.headerText || !!this.ctaInfo) count++;
    if (!!this.imageUrl || !!this.videoId) count++;
    if (!!this.descriptionText) count++;
    return count;
  }
}
Bonnie.defineElement();
